import {AuthNgrx, emptyAuth, getRoles} from '~model/ngrx/auth-ngrx';
import {Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {DateUtil} from '~utils/date-util';
import {BehaviorSubject, interval, Observable} from 'rxjs';
import {MD5} from '~utils/encode';
import {ParamService} from '~core/utils/param.service';
import {AppConfigService} from '~core/background/app-config.service';
import {VERSION} from '~env/version';
import {environment} from '~env/environment';
import { getProfileUrl } from '~utils/user-utils';
import {checkRoles, getDetailsScopes, Paths} from '~core/security/auth.roles';
import { assetUrl } from 'src/single-spa/asset-url';
import {JwtHelperService} from '@auth0/angular-jwt';
import { RotaVo } from '~model/vo/rota-vo';

export interface AppState {
  raw: string;
  version: string;
  description: string;
  profileUrl: string;
  logo?: string;
  auth: AuthNgrx;
  carregando: boolean;
  exibirLayout: boolean;
  menuEstaExpandido: boolean;
  relogioTempo: Date;
  autoUpdate: boolean;
  filtros: string[] | null;
  filtrosCarregados: boolean;
  loaded: boolean;
  inputFiltro: string;
  statusFilterForm: string;
  rotasFiltradas: RotaVo[];
}

@Injectable()
export class AppStore extends ComponentStore<AppState> {

  readonly roles = new BehaviorSubject<{ [k: string]: boolean }>({});

  readonly theme = new BehaviorSubject<string>('');
  readonly authState: Observable<AuthNgrx>;

  readonly dateHasChangedObserver = new BehaviorSubject<boolean>(false);

  constructor(
    paramService: ParamService,
    private appConfig: AppConfigService,
    private jwt: JwtHelperService
  ) {
    super({
      version: VERSION.version,
      description: environment.description,
      raw: VERSION.raw,
      profileUrl: assetUrl('./images/profile.png'),
      auth: emptyAuth(),
      carregando: true,
      exibirLayout: false,
      menuEstaExpandido: false,
      relogioTempo: paramService.dateMonitoramento,
      autoUpdate: paramService.isNow,
      filtros: null,
      filtrosCarregados: false,
      loaded: false,
      inputFiltro: '',
      statusFilterForm: '',
      rotasFiltradas: [],
    });

    this.authState = this.select(state => {
      const roles: { [k: string]: boolean } = {};
      const aux = getRoles(state.auth?.token || '');
      const {scopes, theme} = getDetailsScopes(this.jwt, state.auth.tokenRules || '');
      Object.values(Paths).forEach(path => roles[path] = checkRoles('/'.concat(path), aux, scopes));
      this.roles.next(roles);
      this.theme.next(theme ?? '');
      return state.auth;
    });
    this.onRelogio();
  }

  public inicializar(): void {
        this.appConfig.selectFiltrosVisiveis().subscribe(filtros => {
      this.setState(state => ({ ...state, filtros }));
    });
    this.appConfig.selectConfigCarregado().subscribe(filtrosCarregados => {
      this.setState(state => ({ ...state, filtrosCarregados }));
    });
  }

  public setAuth(auth: AuthNgrx): void {
    this.setState(state => ({...state, auth}));
    this.updateProfileUrl();
  }

  public setCarregando(carregando: boolean): void {
    this.setState(state => ({...state, carregando}));
  }

  public setExibirLayout(exibirLayout: boolean): void {
    this.setState(state => ({...state, exibirLayout}));
  }

  public setMenuExpandido(expandido?: boolean): void {
    this.setState(state => ({...state, menuEstaExpandido: expandido || !state.menuEstaExpandido}));
  }

  public getState(): AppState {
    return this.get();
  }

  public updateProfileUrl(color?: string, bg?: string): void {
    const auth = this.get().auth;
    const hash = auth?.email ? MD5(auth.email || '') : '000';
    this.setState(state => ({...state, profileUrl: getProfileUrl(auth.nickname || '', hash, color, bg)}));
  }

  public updateLogo(logo?: string): void {
    this.setState(state => ({...state, logo}));
  }

  updateRelogio(date: Date, isNow: boolean): void {
    const currentDate = this.getState().relogioTempo.toISOString().split('T')[0];
    const newDate = date.toISOString().split('T')[0];

    if (currentDate !== newDate) {
      this.dateHasChangedObserver.next(true);
    }

    this.setState(state => ({...state, autoUpdate: isNow, relogioTempo: date}));
  }

  private onRelogio(): void {
    interval(1000).subscribe(_ => {
      this.setState(state => ({
        ...state,
        relogioTempo: state.autoUpdate ? DateUtil.now() : state.relogioTempo,
      }));
    });
  }

  updateRotasFiltradas(rotasFiltradas: RotaVo[], loaded: boolean): void {
    this.setState(state => ({...state, loaded, rotasFiltradas}));
  }

  updateInputFilter(inputFiltro: string): void {
    this.setState(state => ({...state, inputFiltro}));
  }

  updateStatusFilter(statusFilterForm: string): void {
    this.setState(state => ({...state, statusFilterForm}));
  }
}
